import { Link, Stack, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { RoutePreview } from '@/components/routes/route-preview';
import { getApiUrl } from '@/lib/api-url';
import type { RouteBounds, RouteLineString } from '@sixam/shared/route-core';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';

type SharedRoute = {
  description: string | null;
  name: string;
  tenant: { name: string; slug: string } | null;
  version: {
    bounds: RouteBounds;
    distance_m: number;
    elevation_gain_m: number | null;
    geometry: RouteLineString;
    version: number;
  };
};

export default function SharedRouteScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const [route, setRoute] = React.useState<SharedRoute | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    void fetch(getApiUrl(`/api/routes/shared/${encodeURIComponent(token)}`))
      .then(async (response) => {
        const body = (await response.json()) as { error?: string; route?: SharedRoute };
        if (!response.ok || !body.route) throw new Error(body.error ?? 'Route unavailable.');
        if (active) setRoute(body.route);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Route unavailable.');
      });
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <ScrollView
      className="flex-1 bg-background"
      contentContainerClassName="mx-auto min-h-full w-full max-w-3xl justify-center gap-5 p-5"
      contentInsetAdjustmentBehavior="automatic"
    >
      <Stack.Screen options={{ title: route?.name ?? '6am route' }} />
      {!route && !error ? <ActivityIndicator /> : null}
      {error ? (
        <Text className="text-destructive" selectable>
          {error}
        </Text>
      ) : null}
      {route ? (
        <>
          <View className="gap-1">
            <Text variant="label" className="text-muted-foreground">
              {route.tenant?.name ?? '6am route'}
            </Text>
            <Text variant="h1">{route.name}</Text>
            {route.description ? <Text variant="muted">{route.description}</Text> : null}
          </View>
          <RoutePreview
            distanceM={route.version.distance_m}
            elevationGainM={route.version.elevation_gain_m}
            geometry={route.version.geometry}
            name={route.name}
          />
          <View className="gap-2 border-y border-border py-4">
            <Text className="font-bold">Plan your next run with 6am</Text>
            <Text variant="muted">
              Join a club, build routes, and share the course before everyone meets.
            </Text>
          </View>
          <Link href="/sign-up" asChild>
            <Button>
              <Text>Join 6am</Text>
            </Button>
          </Link>
        </>
      ) : null}
    </ScrollView>
  );
}
